import api from './axios';

/**
 * Helper function to extract error message from API response
 */
export const getMessageErrorMessage = (error) => {
  if (error && error.response && error.response.data) {
    if (error.response.data.message) {
      return error.response.data.message;
    }
    if (error.response.data.errors && Array.isArray(error.response.data.errors)) {
      return error.response.data.errors[0];
    }
    if (error.response.data.errors && typeof error.response.data.errors === "object") {
      const firstError = Object.values(error.response.data.errors)[0];
      if (Array.isArray(firstError)) {
        return firstError[0];
      }
      return String(firstError);
    }
  }
  return error?.message || "An unexpected error occurred";
};

const hasMessage = true;  // message dikhana hai ya nahi

/**
 * Check if "Thank you for your message" should be shown
 * Shows message if:
 * 1. Consumer has sent at least one message AFTER the last operator reply
 * 2. That message has NO reply after it
 * 3. At least ONE such consumer message has been unreplied for 5+ minutes
 */
export const isShowConsumerToMessage = (messages = []) => {
  if (!Array.isArray(messages) || messages.length === 0) {
    return false;
  }

  // Helper to parse timestamp as UTC (handles both with and without Z suffix)
  const parseAsUTC = (timestamp) => {
    if (!timestamp) return 0;
    const ts = String(timestamp);
    // If already has timezone info (Z or +/-), parse directly
    if (ts.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(ts)) {
      return new Date(ts).getTime();
    }
    // Otherwise, append Z to interpret as UTC
    return new Date(ts + 'Z').getTime();
  };

  // Find the timestamp of the last operator (outbound) message
  const outboundMessages = messages.filter(msg => msg.direction === 'outbound');
  const lastOutboundTime = outboundMessages.length > 0
    ? Math.max(...outboundMessages.map(msg => parseAsUTC(msg.timestamp)))
    : 0;

  // Only check consumer messages AFTER the last operator reply
  const consumerMessagesSinceLastReply = messages.filter(
    msg => msg.direction === 'inbound' && 
           parseAsUTC(msg.timestamp) > lastOutboundTime
  );

  if (consumerMessagesSinceLastReply.length === 0) {
    return false;
  }

  // Check if any of these unreplied consumer messages is 5+ minutes old
  const currentTime = Date.now();
  for (const consumerMsg of consumerMessagesSinceLastReply) {
    if (!consumerMsg.timestamp) continue;

    const messageTime = parseAsUTC(consumerMsg.timestamp);
    const timeDifferenceMinutes = (currentTime - messageTime) / (1000 * 60);

    if (timeDifferenceMinutes >= 5) {
      console.log('[isShowConsumerToMessage] Found unreplied message > 5 min:', consumerMsg.timestamp, 'age:', timeDifferenceMinutes.toFixed(1), 'min');
      return true; // ✅ show ack message
    }
  }

  return false; // ✅ No unreplied messages > 5 min since last reply
};

/**
 * Messages API service
 * Handles all message-related operations
 */
export const messagesApi = {
  // Get recent messages for a conversation
  getConversationMessages: async (conversationId, count = 50) => {
    try {
      const response = await api.get(`/messages/conversation/${conversationId}?count=${count}`);
      return response.data;
    } catch (error) {
      return { error: getMessageErrorMessage(error) };
    }
  },

  // Get paginated messages for a conversation (for lazy loading)
  getPaginatedMessages: async (conversationId, skip = 0, take = 20) => {
    try {
      const response = await api.get(`/messages/conversation/${conversationId}/paginated?skip=${skip}&take=${take}`);
      return response.data;
    } catch (error) {
      return { error: getMessageErrorMessage(error) };
    }
  },

  

  // // Send a message as an operator
  sendOperatorMessage: async (data) => {
    console.log("🔧 messagesApi.sendOperatorMessage called with:", data);
    console.log("🎯 About to POST to: /messages/operator/send");
    try {
      const response = await api.post('/messages/operator/send', data);
      console.log("✅ Operator message API response:", response.data);
      return response.data;
    } catch (error) {
      console.error("❌ Operator message API error:", error);
      return { error: getMessageErrorMessage(error) };
    }
  },

  // Send a message as a consumer
  sendConsumerMessage: async (data, getConsumerToken, config) => {
    console.log("🔧 messagesApi.sendConsumerMessage called with:", data);
    console.log("🎯 About to POST to: /messages/consumer/send");
    
    // Debug: Check each property of the data
    console.log("🔍 Data properties:");
    console.log("  - ConversationId:", data.ConversationId || data.conversationId);
    console.log("  - Body:", data.Body || data.body);
    console.log("  - ConsumerId:", data.ConsumerId || data.consumerId);
    console.log("  - ClientId:", data.ClientId || data.clientId);
    
    try {
      // Get token using helper function or fallback
      const token = getConsumerToken?.() || localStorage.getItem("consumerToken");
      console.log("🔑 Token for API call:", token ? "Token found" : "No token found");
      
      const headers = {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...(config?.clientId && { "X-Client-Id": config.clientId.toString() }),
      };
      
      console.log("📋 Request headers:", headers);
      
      // Use fetch instead of axios to have full control over headers
      const response = await fetch('/api/messages/consumer/send', {
        method: 'POST',
        headers,
        body: JSON.stringify(data),
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }
      
      const result = await response.json();
      console.log("✅ Consumer message API response:", result);
      return result;
    } catch (error) {
      console.error("❌ Consumer message API error:", error);
      return { error: getMessageErrorMessage(error) };
    }
  },

  // Mark a specific message as delivered
  markMessageAsDelivered: async (messageId) => {
    try {
      const response = await api.patch(`/messages/${messageId}/delivered`);
      return response.data;
    } catch (error) {
      return { error: getMessageErrorMessage(error) };
    }
  },

  // Mark all messages in a conversation as delivered
  markAllMessagesAsDelivered: async (conversationId) => {
    try {
      const response = await api.patch(`/messages/conversation/${conversationId}/delivered`);
      return response.data;
    } catch (error) {
      return { error: getMessageErrorMessage(error) };
    }
  },

  // Get unread message count for a conversation
  getUnreadMessageCount: async (conversationId) => {
    try {
      const response = await api.get(`/messages/conversation/${conversationId}/unread-count`);
      return response.data;
    } catch (error) {
      return { error: getMessageErrorMessage(error) };
    }
  },

  // Get operator's sent message count (for the logged-in operator)
  getOperatorSentCount: async () => {
    try {
      const response = await api.get('/messages/operator/sent-count');
      return response.data;
    } catch (error) {
      return { error: getMessageErrorMessage(error) };
    }
  },

  // Get consumer credit balance with cache-busting (always fresh data)
  getConsumerCreditBalance: async (consumerId) => {
    try {
      console.log("💳 Fetching fresh credit balance for consumer ID:", consumerId);
      const response = await api.get(`/consumer/${consumerId}/credits/balance`, {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        },
        // Add timestamp to URL to prevent browser caching
        params: {
          _t: Date.now()
        }
      });
      console.log("✅ Fresh consumer credit balance fetched:", response.data);
      return response.data;
    } catch (error) {
      console.error("❌ Error fetching consumer credit balance:", error);
      return { error: getMessageErrorMessage(error) };
    }
  },
};
